import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  Calculator,
  DollarSign,
  TrendingUp,
  Clock,
  Users,
  BarChart3,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  PiggyBank,
  AlertTriangle,
  Info,
  Building2,
  Briefcase,
  ShoppingCart,
  Plane,
  Heart,
  Home,
  Wrench,
  Package,
  Sparkles
} from 'lucide-react'

interface CalculatorInputs {
  industry: string
  annualRevenue: number
  employeeCount: number
  currentAnalysisSpend: number
  strategicDecisionsPerYear: number
  averageDecisionValue: number
  timeSpentOnAnalysis: number
}

interface ROIResults {
  annualSavings: number
  timeSaved: number
  roiPercentage: number
  paybackPeriod: number
  fiveYearValue: number
  consultingCostAvoidance: number
  betterDecisionValue: number
  opportunityCostSavings: number
}

export const ROICalculatorPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [showResults, setShowResults] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [animatedValues, setAnimatedValues] = useState<ROIResults>({
    annualSavings: 0,
    timeSaved: 0,
    roiPercentage: 0,
    paybackPeriod: 0,
    fiveYearValue: 0,
    consultingCostAvoidance: 0,
    betterDecisionValue: 0,
    opportunityCostSavings: 0
  })

  const [inputs, setInputs] = useState<CalculatorInputs>({
    industry: 'technology',
    annualRevenue: 5000000,
    employeeCount: 25,
    currentAnalysisSpend: 50000,
    strategicDecisionsPerYear: 12,
    averageDecisionValue: 250000,
    timeSpentOnAnalysis: 40
  })

  const industries = [
    { value: 'technology', label: 'Technology & Software', icon: <Package />, multiplier: 1.2 },
    { value: 'manufacturing', label: 'Manufacturing', icon: <Building2 />, multiplier: 1.1 },
    { value: 'retail', label: 'Retail & E-commerce', icon: <ShoppingCart />, multiplier: 1.15 },
    { value: 'healthcare', label: 'Healthcare', icon: <Heart />, multiplier: 1.25 },
    { value: 'realestate', label: 'Real Estate', icon: <Home />, multiplier: 1.3 },
    { value: 'automotive', label: 'Automotive', icon: <Wrench />, multiplier: 1.1 },
    { value: 'aerospace', label: 'Aerospace', icon: <Plane />, multiplier: 1.4 },
    { value: 'consulting', label: 'Consulting', icon: <Briefcase />, multiplier: 1.35 }
  ]

  const calculateROI = (): ROIResults => {
    const industryMultiplier = industries.find(i => i.value === inputs.industry)?.multiplier || 1
    const stratiqxCost = 15000 // Annual StratIQX cost
    
    // Consulting cost avoidance
    const typicalConsultingCost = inputs.strategicDecisionsPerYear * 35000 // Average per-project consulting cost
    const consultingCostAvoidance = typicalConsultingCost - stratiqxCost
    
    // Time savings value
    const executiveHourlyRate = inputs.annualRevenue / 2080 / inputs.employeeCount * 5 // Rough executive rate
    const hoursPerDecision = inputs.timeSpentOnAnalysis / inputs.strategicDecisionsPerYear
    const hoursSaved = hoursPerDecision * inputs.strategicDecisionsPerYear * 0.75 // 75% time reduction
    const timeSavedValue = hoursSaved * executiveHourlyRate
    
    // Better decision value (10-15% improvement in decision quality)
    const decisionQualityImprovement = 0.12 * industryMultiplier
    const betterDecisionValue = inputs.averageDecisionValue * inputs.strategicDecisionsPerYear * decisionQualityImprovement
    
    // Opportunity cost savings (faster decisions capture more opportunities)
    const opportunityCostSavings = inputs.averageDecisionValue * 0.2 * industryMultiplier
    
    // Total annual savings
    const annualSavings = consultingCostAvoidance + timeSavedValue + betterDecisionValue + opportunityCostSavings
    
    // ROI calculations
    const roiPercentage = ((annualSavings - stratiqxCost) / stratiqxCost) * 100
    const paybackPeriod = stratiqxCost / (annualSavings / 12) // in months
    const fiveYearValue = annualSavings * 5 - (stratiqxCost * 5)
    
    return {
      annualSavings: Math.round(annualSavings),
      timeSaved: Math.round(hoursSaved),
      roiPercentage: Math.round(roiPercentage),
      paybackPeriod: Math.round(paybackPeriod * 10) / 10,
      fiveYearValue: Math.round(fiveYearValue),
      consultingCostAvoidance: Math.round(consultingCostAvoidance),
      betterDecisionValue: Math.round(betterDecisionValue),
      opportunityCostSavings: Math.round(opportunityCostSavings)
    }
  }

  const handleCalculate = () => {
    const results = calculateROI()
    setShowResults(true)
    
    // Animate the values
    const duration = 2000
    const steps = 60
    const interval = duration / steps
    let currentStep = 0
    
    const timer = setInterval(() => {
      currentStep++
      const progress = currentStep / steps
      
      setAnimatedValues({
        annualSavings: Math.round(results.annualSavings * progress),
        timeSaved: Math.round(results.timeSaved * progress),
        roiPercentage: Math.round(results.roiPercentage * progress),
        paybackPeriod: Math.round(results.paybackPeriod * progress * 10) / 10,
        fiveYearValue: Math.round(results.fiveYearValue * progress),
        consultingCostAvoidance: Math.round(results.consultingCostAvoidance * progress),
        betterDecisionValue: Math.round(results.betterDecisionValue * progress),
        opportunityCostSavings: Math.round(results.opportunityCostSavings * progress)
      })
      
      if (currentStep >= steps) {
        clearInterval(timer)
        setAnimatedValues(results)
      }
    }, interval)
  }

  const handleInputChange = (field: keyof CalculatorInputs, value: string | number) => {
    setInputs(prev => ({
      ...prev,
      [field]: field === 'industry' ? value : Number(value)
    }))
    setShowResults(false)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6 inline-flex items-center bg-blue-600/20 px-4 py-2 rounded-full border border-blue-500/30">
            <Calculator className="h-5 w-5 text-cyan-400 mr-2" />
            <span className="text-cyan-400 font-semibold">Strategic Intelligence ROI Calculator</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white to-cyan-300 bg-clip-text text-transparent">
            Calculate Your Strategic Intelligence ROI
          </h1>
          
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Discover how much StratIQX can save your business in consulting costs, time, and missed opportunities. 
            Get a personalized ROI projection in under 60 seconds.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">327%</div>
              <div className="text-sm text-gray-400">Average ROI</div>
            </div>
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">2.8mo</div>
              <div className="text-sm text-gray-400">Avg Payback Period</div>
            </div>
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">$385K</div>
              <div className="text-sm text-gray-400">Avg Annual Savings</div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div>
              <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white flex items-center">
                    <Briefcase className="h-6 w-6 mr-3" />
                    Your Business Profile
                  </h2>
                </div>
                <CardContent className="p-6 space-y-6">
                  {/* Industry Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Industry
                    </label>
                    <select
                      value={inputs.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    >
                      {industries.map(ind => (
                        <option key={ind.value} value={ind.value}>{ind.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Annual Revenue */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Annual Revenue
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.annualRevenue}
                        onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="100000"
                        step="100000"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Company's annual revenue</p>
                  </div>

                  {/* Employee Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Number of Employees
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.employeeCount}
                        onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Current Analysis Spend */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Current Analysis/Consulting Spend (Annual)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.currentAnalysisSpend}
                        onChange={(e) => handleInputChange('currentAnalysisSpend', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="0"
                        step="10000"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Include consultants, analysts, research reports</p>
                  </div>

                  {/* Strategic Decisions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Strategic Decisions Per Year
                    </label>
                    <div className="relative">
                      <Target className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.strategicDecisionsPerYear}
                        onChange={(e) => handleInputChange('strategicDecisionsPerYear', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Major decisions requiring analysis</p>
                  </div>

                  {/* Average Decision Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Average Value Per Decision
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.averageDecisionValue}
                        onChange={(e) => handleInputChange('averageDecisionValue', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="10000"
                        step="10000"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Revenue impact or cost at stake</p>
                  </div>

                  {/* Time Spent on Analysis */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Hours Spent on Analysis (Monthly)
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={inputs.timeSpentOnAnalysis}
                        onChange={(e) => handleInputChange('timeSpentOnAnalysis', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-blue-700/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        min="1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leadership time on research & analysis</p>
                  </div>

                  <Button
                    onClick={handleCalculate}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white py-4 text-lg font-semibold"
                  >
                    <Calculator className="h-5 w-5 mr-2" />
                    Calculate My ROI
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Results Section */}
            <div>
              {!showResults ? (
                <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm h-full flex items-center justify-center">
                  <CardContent className="text-center p-12">
                    <div className="bg-blue-600/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <BarChart3 className="h-10 w-10 text-cyan-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Your ROI Results Will Appear Here
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Fill in your business profile and click "Calculate My ROI" to see your personalized savings projection
                    </p>
                    <div className="flex items-center justify-center text-cyan-400">
                      <Info className="h-5 w-5 mr-2" />
                      <span className="text-sm">Takes less than 60 seconds</span>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Main ROI Card */}
                  <Card className="!bg-gradient-to-br !from-cyan-800/80 !to-blue-800/80 border-cyan-400/50 backdrop-blur-sm">
                    <CardContent className="p-6">
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center bg-green-600/20 px-4 py-2 rounded-full border border-green-500/30 mb-4">
                          <Sparkles className="h-5 w-5 text-green-400 mr-2" />
                          <span className="text-green-400 font-semibold">Your Projected ROI</span>
                        </div>
                        <div className="text-6xl font-bold text-white mb-2">
                          {animatedValues.roiPercentage}%
                        </div>
                        <p className="text-gray-300">Return on Investment</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-800/50 p-4 rounded-lg">
                          <div className="text-3xl font-bold text-cyan-400">
                            {formatCurrency(animatedValues.annualSavings)}
                          </div>
                          <div className="text-sm text-gray-400">Annual Savings</div>
                        </div>
                        <div className="bg-slate-800/50 p-4 rounded-lg">
                          <div className="text-3xl font-bold text-green-400">
                            {animatedValues.paybackPeriod} mo
                          </div>
                          <div className="text-sm text-gray-400">Payback Period</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Breakdown Cards */}
                  <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                      <h3 className="text-xl font-bold text-white flex items-center">
                        <PiggyBank className="h-5 w-5 mr-2" />
                        Savings Breakdown
                      </h3>
                    </div>
                    <CardContent className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-700">
                        <div className="flex items-center">
                          <DollarSign className="h-5 w-5 text-yellow-400 mr-3" />
                          <span className="text-gray-300">Consulting Cost Avoidance</span>
                        </div>
                        <span className="text-xl font-bold text-yellow-400">
                          {formatCurrency(animatedValues.consultingCostAvoidance)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-3 border-b border-slate-700">
                        <div className="flex items-center">
                          <Target className="h-5 w-5 text-cyan-400 mr-3" />
                          <span className="text-gray-300">Better Decision Value</span>
                        </div>
                        <span className="text-xl font-bold text-cyan-400">
                          {formatCurrency(animatedValues.betterDecisionValue)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-3 border-b border-slate-700">
                        <div className="flex items-center">
                          <Clock className="h-5 w-5 text-green-400 mr-3" />
                          <span className="text-gray-300">Time Saved ({animatedValues.timeSaved} hours)</span>
                        </div>
                        <span className="text-xl font-bold text-green-400">
                          {formatCurrency(animatedValues.opportunityCostSavings)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-3">
                        <div className="flex items-center">
                          <TrendingUp className="h-5 w-5 text-purple-400 mr-3" />
                          <span className="text-gray-300">5-Year Total Value</span>
                        </div>
                        <span className="text-xl font-bold text-purple-400">
                          {formatCurrency(animatedValues.fiveYearValue)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="!bg-gradient-to-r !from-cyan-600 !to-blue-600 border-cyan-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-2xl font-bold text-white mb-3">
                        Ready to Capture This Value?
                      </h3>
                      <p className="text-cyan-100 mb-6">
                        Start your strategic intelligence journey today with StratIQX
                      </p>
                      <Button
                        onClick={handleGetStarted}
                        size="lg"
                        className="bg-white text-blue-600 hover:bg-gray-100"
                      >
                        Get Started Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>

          {/* Comparison Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              StratIQX vs Traditional Alternatives
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-slate-800/80 border-red-800/30 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">Big 3 Consulting</h3>
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">$300K-$500K per engagement</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">3-6 month delivery time</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Limited to 1-2 projects/year</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Junior analysts doing the work</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/80 border-yellow-800/30 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-yellow-600 to-amber-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">In-House Analysis</h3>
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">40+ hours/month of leadership time</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Limited analytical expertise</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Unconscious bias in decisions</span>
                    </li>
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Incomplete data access</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/80 border-cyan-500/50 backdrop-blur-sm transform scale-105">
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    StratIQX
                    <Zap className="h-5 w-5 ml-2" />
                  </h3>
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">$15K annual investment</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">4-hour delivery time</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">Unlimited analyses per year</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                      <span className="text-gray-300">AI + expert validation</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  )
}